import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Bell, Plus, Check, Trash2, Mail, MailCheck } from "lucide-react";

export default function Reminders() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", due_date: "", category: "renewal" });

  const load = () => api.get("/reminders").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title || !form.due_date) { toast.error(t("reminder_required")); return; }
    await api.post("/reminders", form); setOpen(false); setForm({ title: "", due_date: "", category: "renewal" }); load();
  };
  const done = async (id) => { await api.put(`/reminders/${id}`, { done: true }); load(); };
  const del = async (id) => { await api.delete(`/reminders/${id}`); load(); };
  const sendNow = async (id) => {
    try {
      await api.post(`/reminders/${id}/send-now`);
      toast.success(t("reminder_email_sent"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("could_not_link"));
    }
  };

  return (
    <AppLayout>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><Bell className="h-7 w-7 text-brandblue" /> {t("reminders")}</h1>
          <p className="mt-1 text-slate-500">{t("reminders_sub")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-reminder-btn" className="rounded-full brand-gradient text-white"><Plus className="h-4 w-4 mr-1" />{t("add")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("new_reminder")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("title")}</Label><Input data-testid="reminder-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("due_date")}</Label><Input data-testid="reminder-date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" /></div>
            </div>
            <DialogFooter><Button data-testid="save-reminder-btn" onClick={add} className="rounded-full brand-gradient text-white">{t("save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-5 flex items-start gap-3 rounded-2xl border border-brandblue/20 bg-brandblue/5 p-4" data-testid="reminders-email-banner">
        <Mail className="h-5 w-5 shrink-0 text-brandblue mt-0.5" />
        <p className="text-sm text-navy">{t("reminders_email_banner")}</p>
      </div>

      <div className="mt-6 space-y-3">
        {items.map((r) => {
          const d = r.due_date ? new Date(r.due_date) : null;
          return (
            <div key={r.id} data-testid={`reminder-${r.id}`} className={`flex items-center gap-4 rounded-2xl bg-white border border-slate-100 card-shadow p-4 ${r.done ? "opacity-50" : ""}`}>
              <div className="flex h-12 w-12 flex-col items-center justify-center rounded-xl bg-slate-50 text-navy">
                <span className="text-[10px] uppercase text-slate-400">{d ? d.toLocaleString("en", { month: "short" }) : ""}</span>
                <span className="text-sm font-semibold leading-none">{d ? d.getDate() : "—"}</span>
              </div>
              <div className="flex-1">
                <p className={`font-medium text-navy ${r.done ? "line-through" : ""}`}>{r.title}</p>
                <p className="text-xs text-slate-400 capitalize flex items-center gap-1.5">
                  {r.category}
                  {r.emailed && <span className="inline-flex items-center gap-1 text-covered"><MailCheck className="h-3 w-3" /> {t("reminder_emailed")}</span>}
                </p>
              </div>
              {!r.done && <button onClick={() => sendNow(r.id)} data-testid={`reminder-email-${r.id}`} title={t("email_me")} className="rounded-full p-2 text-slate-400 hover:text-brandblue hover:bg-blue-50"><Mail className="h-4 w-4" /></button>}
              {!r.done && <button onClick={() => done(r.id)} className="rounded-full p-2 text-slate-400 hover:text-covered hover:bg-emerald-50"><Check className="h-4 w-4" /></button>}
              <button onClick={() => del(r.id)} className="rounded-full p-2 text-slate-300 hover:text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
        {items.length === 0 && <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-400">{t("no_reminders")}</div>}
      </div>
    </AppLayout>
  );
}
